import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import LoadingSpinner from './LoadingSpinner';
import ProfileOption from './ProfileOption';
import { PrimaryButton, SecondaryButton } from './buttons';
import { BaseForm } from './form';

import './Profile.scss';

const Profile = ({
  isWelcomeMessageVisible,
  isDoneButtonVisible = true,
  isLoadingProfiles,
  isManagingProfiles,
  isOpen = false,
  profiles = [],
  selectedProfileId,
  onEditProfile,
  onChangeProfileClick,
  onEnterManageMode,
  onLeaveManageMode,
  onSelectProfile
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (document.activeElement) {
      document.activeElement.blur();
    }
  }, [isManagingProfiles]);

  // Reverted exactly to the original logic
  const handleEditProfile = (profileId) => onEditProfile?.(profileId);
  const handleSelectProfile = (profileId) => onSelectProfile?.(profileId);
  const handleEnterManageMode = () => onEnterManageMode?.();
  const handleLeaveManageMode = () => onLeaveManageMode?.();
  const handleChangeProfile = () => onChangeProfileClick?.();

  const renderHeader = () => {
    const title = isManagingProfiles
      ? t('profile.manageTitle')
      : t('profile.selectTitle');

    return (
      <div
        className={classNames('profile-header', 'page-header', {
          'profile-header--welcome': isWelcomeMessageVisible
        })}
      >
        {isWelcomeMessageVisible && <h1>{t('profile.welcomeTitle')}</h1>}
        {isWelcomeMessageVisible ? <h2>{title}</h2> : <h1>{title}</h1>}
      </div>
    );
  };

  const renderFooterButtons = () => {
    if (isManagingProfiles) {
      return (
        <div
          className={classNames('profile-footer', {
            'profile-footer--editing': isDoneButtonVisible
          })}
        >
          <SecondaryButton
            colorScheme="blue"
            label={t('profile.addProfile')}
            type="button"
            onClick={() => handleEditProfile(null)} // Reverted to explicitly pass null
          />
          
          {!isOpen && (
            <SecondaryButton
              colorScheme="blue"
              label={t('profile.switchProfile', 'Switch Profile')}
              type="button"
              onClick={handleChangeProfile}
            />
          )}

          {isDoneButtonVisible && (
            <PrimaryButton
              colorScheme="blue"
              label={t('profile.done')}
              type="button"
              onClick={handleLeaveManageMode}
            />
          )}
        </div>
      );
    }

    return (
      <div className="profile-footer">
        <SecondaryButton
          colorScheme="blue"
          label={t('profile.manageProfiles')}
          type="button"
          onClick={handleEnterManageMode}
        />
      </div>
    );
  };

  return (
    <div>
      {renderHeader()}
      {isLoadingProfiles ? (
        <div className="profile-container profile-container--loading container">
          <LoadingSpinner colorScheme="green" size="large" />
        </div>
      ) : (
        <div className="profile-container container">
          <BaseForm
            onSubmit={() => undefined}
            initialValues={selectedProfileId ? { profile: `profile_${selectedProfileId}` } : undefined}
          >
            <ul className="icon-chooser">
              {profiles.map((profile) => (
                <li className="icon-chooser-list__item" key={profile.id}>
                  <ProfileOption
                    icon={profile.icon}
                    id={profile.id}
                    isEditing={isManagingProfiles}
                    onEdit={handleEditProfile}
                    onSelect={handleSelectProfile}
                    title={profile.firstName}
                  />
                </li>
              ))}
            </ul>
          </BaseForm>
          {renderFooterButtons()}
        </div>
      )}
    </div>
  );
};

Profile.propTypes = {
  isWelcomeMessageVisible: PropTypes.bool.isRequired,
  isDoneButtonVisible: PropTypes.bool,
  isLoadingProfiles: PropTypes.bool.isRequired,
  isManagingProfiles: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool,
  profiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string
    })
  ).isRequired,
  onEditProfile: PropTypes.func,
  onChangeProfileClick: PropTypes.func,
  onEnterManageMode: PropTypes.func,
  onLeaveManageMode: PropTypes.func,
  onSelectProfile: PropTypes.func
};

export default Profile;
